import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, adminApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface User {
  id: string;
  email: string;
  name?: string;
  role: 'buyer' | 'seller';
  accountRole: 'owner' | 'admin' | 'user';
  permissions: string[];
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface UserFormData {
  email: string;
  name: string;
  role: 'buyer' | 'seller';
  accountRole: 'user' | 'admin';
  permissions: string[];
}

const AVAILABLE_PERMISSIONS = [
  { id: 'manage_users', label: 'Manage Users', description: 'Add, edit, and remove users' },
  { id: 'manage_projects', label: 'Manage Projects', description: 'Create and manage projects' },
  { id: 'manage_items', label: 'Manage Items', description: 'Create and manage items' },
  { id: 'manage_orders', label: 'Manage Orders', description: 'View and manage orders' },
  { id: 'manage_account_settings', label: 'Manage Account Settings', description: 'Modify account settings' },
  { id: 'view_analytics', label: 'View Analytics', description: 'Access analytics and reports' }
];

export const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    role: 'buyer',
    accountRole: 'user',
    permissions: []
  });
  const [submitting, setSubmitting] = useState(false);

  // Check if current user can manage users
  const canManageUsers = currentUser?.accountRole === 'owner' || 
    currentUser?.permissions?.includes('manage_users');


  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersApi.list();
      if (response.data.success) {
        setUsers(response.data.data || []);
      } else {
        setError('Failed to load users');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageUsers) {
      loadUsers();
    }
  }, [canManageUsers, loadUsers]);

  const handleAddUser = () => {
    setFormData({
      email: '',
      name: '',
      role: 'buyer',
      accountRole: 'user',
      permissions: []
    });
    setEditingUser(null);
    setShowAddForm(true);
  };

  const handleEditUser = (user: User) => {
    setFormData({
      email: user.email,
      name: user.name || '',
      role: user.role,
      accountRole: user.accountRole === 'owner' ? 'admin' : user.accountRole,
      permissions: user.permissions
    });
    setEditingUser(user);
    setShowAddForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingUser) {
        // Update existing user
        const { email, ...updateData } = formData;
        const response = await usersApi.update(editingUser.id, updateData);
        if (response.data.success) {
          await loadUsers();
          setShowAddForm(false);
        } else {
          setError(response.data.error?.message || 'Failed to update user');
        }
      } else {
        // Create new user
        const response = await usersApi.create(formData);
        if (response.data.success) {
          await loadUsers();
          setShowAddForm(false);
        } else {
          setError(response.data.error?.message || 'Failed to create user');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}?`)) {
      return;
    }

    try {
      const response = await usersApi.delete(user.id);
      if (response.data.success) {
        await loadUsers();
      } else {
        setError(response.data.error?.message || 'Failed to delete user');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete user');
    }
  };

  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(p => p !== permissionId)
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (accountRole: string) => {
    switch (accountRole) {
      case 'owner': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'admin': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const handleFixAccountRoles = async () => {
    setSubmitting(true);
    try {
      const response = await adminApi.fixAccountRoles();
      if (response.data.success) {
        alert(`Fixed account roles for ${response.data.data?.totalFixed || 0} users. Please refresh the page.`);
        // Refresh the page to get updated user data
        window.location.reload();
      } else {
        alert('Failed to fix account roles: ' + (response.data.error?.message || 'Unknown error'));
      }
    } catch (error: any) {
      alert('Failed to fix account roles: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManageUsers) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">User Management</h2>
        <div className="space-y-4">
          <p className="text-slate-600 dark:text-slate-400">
            You don't have permission to manage users. Contact your account owner for access.
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">Debug Info:</h4>
            <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
              <p><strong>Account Role:</strong> {currentUser?.accountRole || 'undefined'}</p>
              <p><strong>Permissions:</strong> {currentUser?.permissions?.join(', ') || 'none'}</p>
              <p><strong>Email:</strong> {currentUser?.email}</p>
              <p><strong>Can Manage:</strong> {canManageUsers ? 'Yes' : 'No'}</p>
            </div>
            <button
              onClick={handleFixAccountRoles}
              disabled={submitting}
              className="mt-3 btn-primary text-sm"
            >
              {submitting ? 'Fixing...' : 'Fix Account Roles'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">User Management</h2>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">User Management</h2>
        
        {/* Header with Add User Button and View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div>
            {/* Count removed as requested */}
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Add User Button */}
            <button
              onClick={handleAddUser}
              className="btn-primary p-2"
              title="Add User"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
                title="Grid View"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
                title="List View"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* User List */}
      {viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <div key={user.id} className="card p-4">
              {/* User Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                    {user.name || 'No name'}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {user.email}
                  </p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.accountRole)}`}>
                  {user.accountRole}
                </span>
              </div>

              {/* Role */}
              <div className="mb-3">
                <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Role</span>
                <p className="text-sm text-slate-900 dark:text-slate-100 capitalize">{user.role}</p>
              </div>

              {/* Permissions */}
              <div className="mb-3">
                <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Permissions</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {user.permissions.length > 0 ? (
                    user.permissions.slice(0, 3).map((permission) => (
                      <span
                        key={permission}
                        className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
                      >
                        {permission.replace('manage_', '').replace('_', ' ')}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-500 dark:text-slate-400">No permissions</span>
                  )}
                  {user.permissions.length > 3 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      +{user.permissions.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Last Login */}
              <div className="mb-4">
                <span className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Last Login</span>
                <p className="text-sm text-slate-900 dark:text-slate-100">
                  {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                </p>
              </div>

              {/* Actions */}
              {user.id !== currentUser?.id && (
                <div className="flex space-x-2 pt-3 border-t border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => handleEditUser(user)}
                    className="flex-1 btn-secondary text-sm"
                  >
                    Edit
                  </button>
                  {user.accountRole !== 'owner' && (
                    <button
                      onClick={() => handleDeleteUser(user)}
                      className="flex-1 btn-destructive text-sm"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user.name || 'No name'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {user.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.accountRole)}`}>
                        {user.accountRole}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {user.role}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.permissions.length > 0 ? (
                        user.permissions.map((permission) => (
                          <span
                            key={permission}
                            className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
                          >
                            {permission.replace('manage_', '').replace('_', ' ')}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">No permissions</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {user.id !== currentUser?.id && (
                        <>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            Edit
                          </button>
                          {user.accountRole !== 'owner' && (
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {users.length === 0 && (
        <div className="text-center py-8">
          <p className="text-slate-600 dark:text-slate-400">No users found.</p>
        </div>
      )}

      {/* Add/Edit User Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md my-8 mx-auto max-h-[calc(100vh-4rem)] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={!!editingUser}
                  required
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'buyer' | 'seller' }))}
                  className="input"
                >
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                </select>
              </div>

              <div>
                <label className="form-label">Account Role</label>
                <select
                  value={formData.accountRole}
                  onChange={(e) => setFormData(prev => ({ ...prev, accountRole: e.target.value as 'user' | 'admin' }))}
                  className="input"
                  disabled={currentUser?.accountRole !== 'owner'}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                {currentUser?.accountRole !== 'owner' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Only account owners can assign admin roles
                  </p>
                )}
              </div>

              <div>
                <label className="form-label">Permissions</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {AVAILABLE_PERMISSIONS.map((permission) => (
                    <label key={permission.id} className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(permission.id)}
                        onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">{permission.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {permission.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex-1"
                >
                  {submitting ? 'Saving...' : (editingUser ? 'Update User' : 'Add User')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
